/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { formatDateTime } from '@/utils/formatedDates';
import { CheckCircledIcon } from '@radix-ui/react-icons';
import componentsArr from 'data/components-library.json';
import servicesArr from 'data/services-library.json';
import { Apps, Component } from 'types/api-data';

import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { Card, Logo } from '../cards';
import { System } from '../icons';
import { ComponentDetailModal } from './component-detail-modal';

const dispFirstAndRest = (data: Apps[]) => {
  let name = '';

  if (data.length === 1) {
    name = `${data[0].name}`;
  } else if (data.length === 2)
    name = `${data[0].name}  +${data.length - 1} app`;
  else name = `${data[0].name}  +${data.length - 1} apps`;

  if (name.indexOf('+') >= 0 && name.slice(0, name.indexOf('+')).length >= 20) {
    return (
      name.slice(0, name.indexOf('+')).slice(0, 20) +
      '...' +
      name.slice(name.indexOf('+'), name.length)
    );
  } else return name.slice(0, 25) + '...';
};

export const ConfiguredComponentsCard = ({
  component,
  onSuccess,
}: {
  component: Component;
  onSuccess?: () => void;
}) => {
  const [showModal, setShowModal] = useState(false);
  const { messages } = useLanguage();
  const generalIntl = messages?.General;
  const componentData = messages?.ComponentsPage;

  const framework = component.properties?.included_workflow_types;

  const matchedService = servicesArr?.services.find(
    (p) => p.type === component.served_by?.type
  );

  const matchedComponent = componentsArr.components.find(
    (c) => c.type === component.type
  );
  const checkIsWorkflow = component?.type.includes('workflow');

  const renderCardContent = () => (
    <Card type={component.type} autoDetected={component.auto_discovered}>
      <div className="space-y-0.5">
        <p className="text-body font-normal text-body-subtle">
          {matchedComponent?.type_display_name}
        </p>
        <h5 className="text-h4 font-medium text-body-primary">
          {component?.display_name}
        </h5>
        {component.apps?.length ? (
          <p className="text-body-sm font-normal text-form-input-disabled">
            {dispFirstAndRest(component.apps)}
          </p>
        ) : null}
      </div>
    </Card>
  );
  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {checkIsWorkflow ? (
              <Link href={`/components/${component?.component_name}`}>
                {renderCardContent()}
              </Link>
            ) : (
              <div
                className="cursor-pointer"
                onClick={() => setShowModal(true)}
              >
                {renderCardContent()}
              </div>
            )}
          </TooltipTrigger>
          <TooltipContent
            align="start"
            className="min-w-[480px] space-y-4 rounded-lg border border-line-secondary bg-canvas-background-secondary px-[30px] pb-8 pt-6"
          >
            {/* Component Header */}
            <div className="flex items-center gap-3 pb-2">
              <Logo
                type={component.type}
                className="border-brand-gray-03 border"
                size={{ img: 'size-10', bg: 'size-16' }}
                autoDetected={component.auto_discovered}
              />
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-body leading-[19.6px] text-body-subtle">
                    {matchedComponent?.type_display_name}
                  </span>
                  {matchedComponent?.type_display_name && (
                    <div className="h-4 border-l border-form-control-border-disabled" />
                  )}
                  <span className="text-body-sm text-form-input-disabled">
                    {component?.type}
                  </span>
                </div>

                <h2 className="text-2xl text-body-primary">
                  {component?.display_name}
                </h2>

                <div className="mt-1">
                  {component?.status === 'active' && (
                    <p className="flex items-center gap-1 text-form-input-text text-body-subtle">
                      <CheckCircledIcon className="size-[18px] text-[#57A300]" />
                      {generalIntl?.active}
                    </p>
                  )}
                  {/* //Todo: We have to replace this static version with the api res once its available  */}
                  {/* <span className="text-body leading-[19.6px] text-body-subtle">
                    {generalIntl?.version}: 6.4
                  </span> */}
                </div>
              </div>
            </div>
            {/* Last Updated */}
            <div className="flex items-start gap-2 rounded-lg border border-line-secondary bg-canvas-background-primary p-3">
              {System.InfoIcon('size-6 text-form-input-disabled')}
              <div className="flex flex-col">
                <div className="text-body-subtle">
                  {generalIntl?.last_updated}:
                  <span className="ml-1">
                    {component.last_updated_at
                      ? formatDateTime(component.last_updated_at)
                      : generalIntl?.not_available}
                  </span>
                </div>
                <div className="text-body-subtle">
                  {generalIntl?.created}:
                  <span className="ml-1">
                    {component.created_at
                      ? formatDateTime(component.created_at)
                      : generalIntl?.not_available}
                  </span>
                </div>
              </div>
            </div>
            {/* Services Info */}
            <div className="space-y-4 divide-y divide-line-tertiary">
              {component?.served_by && (
                <div className="pb-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-h6 leading-4 text-[#C1C9CF]">
                      {generalIntl?.service}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Logo
                        type={component.served_by.type}
                        className="border border-[#40565A]"
                        size={{ img: 'size-5', bg: 'size-8' }}
                      />
                      <div className="flex flex-col">
                        <span className="text-body leading-[140%] text-body-primary">
                          {component.served_by.display_name}
                        </span>
                        <span className="text-h6 leading-4 text-form-input-disabled">
                          {matchedService?.service_display_name}{' '}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Frameworks info */}
              {framework && framework?.length > 1 ? (
                <div
                  className={cn('space-y-1', component?.served_by && 'pt-4')}
                >
                  <h3 className="text-form-input-disabled">
                    {componentData?.framework_or_api}
                  </h3>
                  <div className="flex gap-2">
                    {framework.map((item) => (
                      <Logo
                        key={item}
                        type={item}
                        size={{ img: 'size-6', bg: 'size-8' }}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
              {/* App Info */}
              {component?.apps && component.apps.length > 1 ? (
                <div className="flex items-center pt-4 text-body leading-[19.6px]">
                  <span className="text-form-input-disabled">
                    {componentData?.application_associated}:
                  </span>
                  <span className="ml-1 text-body-primary">
                    {component?.apps?.length}
                  </span>
                </div>
              ) : component?.apps && component.apps.length === 1 ? (
                <div className="flex flex-col gap-1 pt-4">
                  <span className="text-h6 capitalize leading-4 tracking-[2%] text-form-input-disabled">
                    {generalIntl?.application}
                  </span>
                  <span className="text-h4 font-medium text-body-primary">
                    {component?.apps[0].display_name}
                  </span>
                </div>
              ) : null}

              {component?.auto_discovered && (
                <p className="pt-4 text-body-sm tracking-[2%] text-form-input-disabled">
                  {componentData?.auto_discovered_info_text}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {showModal && (
        <ComponentDetailModal
          typeName={matchedComponent?.type_display_name}
          component={component}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
          }}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
};
